import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import MarkdownEditor from "../../../components/Markdown/MarkdownEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faTimes } from "@fortawesome/free-solid-svg-icons";

import axios from "../../../public Func/axiosAuth";
import "./Article.css";

function ArticleEditModal({ article, onClose, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const [uploadingAttachedImage, setUploadingAttachedImage] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const [formData, setFormData] = useState({
    categoryID: article.categoryID ? article.categoryID.toString() : "",
    title: article.title || "",
    mainText: article.mainText || "",
    coverImage: article.coverImage || "",
    date: (article.date || new Date().toISOString()).split("T")[0],
    attachedImage:
      article.images && Array.isArray(article.images)
        ? article.images.map((img) =>
            typeof img === "string" ? { name: img, link: img } : { name: img.name, link: img.link },
          )
        : [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, articleRes] = await Promise.all([
          axios.get(process.env.REACT_APP_BACKEND_URL + "/blog/category-list"),
          axios.get(
            process.env.REACT_APP_BACKEND_URL +
              "/blog/article?articleID=" +
              article.id,
          ),
        ]);
        const list = categoriesRes.data || [];
        setCategories(list);

        const fullArticle = articleRes.data || {};
        const fullImages =
          fullArticle.images && Array.isArray(fullArticle.images)
            ? fullArticle.images.map((img) =>
                typeof img === "string"
                  ? { name: img, link: img }
                  : { name: img.name, link: img.link },
              )
            : formData.attachedImage;

        setFormData({
          categoryID:
            formData.categoryID ||
            (fullArticle.categoryID
              ? fullArticle.categoryID.toString()
              : list.length > 0
                ? list[0].id.toString()
                : ""),
          title: fullArticle.title || article.title || "",
          mainText: fullArticle.mainText || article.mainText || "",
          coverImage: fullArticle.coverImage || article.coverImage || "",
          date: (fullArticle.date || article.date || new Date().toISOString()).split("T")[0],
          attachedImage: fullImages,
        });
        setLoading(false);
      } catch (err) {
        console.error("Error fetching article edit data:", err);
        setMessage({
          type: "error",
          text: "Failed to load article data. Please try again.",
        });
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setMessage({ type: "", text: "" });
  };

  // MarkdownEditor reports its contents as a Markdown string rather than a DOM
  // event, so it cannot go through handleInputChange, which reads name/value off
  // e.target and throws on a plain string.
  const handleMarkdownChange = (value) => {
    setFormData((prev) => ({ ...prev, mainText: value }));
    setMessage({ type: "", text: "" });
  };

  const uploadImage = async (file) => {
    const uploadData = new FormData();
    uploadData.append("file", file);
    const res = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "/upload",
      uploadData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return typeof res.data === "string"
      ? res.data
      : res.data?.link || res.data?.file || res.data?.filename || res.data;
  };

  const handleCoverImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage({ type: "error", text: "Please select an image file." });
      return;
    }

    try {
      setUploadingCoverImage(true);
      setMessage({ type: "", text: "" });
      const imageUrl = await uploadImage(file);
      setFormData((prev) => ({ ...prev, coverImage: imageUrl }));
    } catch (err) {
      console.error("Error uploading cover image:", err);
      setMessage({
        type: "error",
        text: err.response?.data || "Failed to upload cover image. Please try again.",
      });
    } finally {
      setUploadingCoverImage(false);
    }
  };

  const handleAttachedImageAdd = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage({ type: "error", text: "Please select an image file." });
      return;
    }

    try {
      setUploadingAttachedImage(true);
      setMessage({ type: "", text: "" });
      const imageUrl = await uploadImage(file);
      setFormData((prev) => ({
        ...prev,
        attachedImage: [...prev.attachedImage, { name: file.name, link: imageUrl }],
      }));
    } catch (err) {
      console.error("Error uploading image:", err);
      setMessage({
        type: "error",
        text: err.response?.data || "Failed to upload image. Please try again.",
      });
    } finally {
      setUploadingAttachedImage(false);
      e.target.value = "";
    }
  };

  const handleImageRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachedImage: prev.attachedImage.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.categoryID) {
      setMessage({ type: "error", text: "Please select a category." });
      return;
    }
    if (!formData.title.trim()) {
      setMessage({ type: "error", text: "Please enter article title." });
      return;
    }
    if (!formData.mainText.trim()) {
      setMessage({ type: "error", text: "Please enter article content." });
      return;
    }
    if (!formData.date) {
      setMessage({ type: "error", text: "Please select a date." });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const payload = {
        articleID: article.id,
        categoryID: parseInt(formData.categoryID),
        title: formData.title.trim(),
        mainText: formData.mainText.trim(),
        coverImage: formData.coverImage.trim() || null,
        date: new Date(formData.date).toISOString(),
        attachedImage:
          formData.attachedImage.length > 0 ? formData.attachedImage : undefined,
      };

      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/admin/edit-article",
        payload,
      );

      if (
        res.status === 200 &&
        (res.data === "Done" || res.data === "done" || res.data?.done)
      ) {
        const category =
          categories.find(
            (c) => c.id.toString() === formData.categoryID.toString(),
          )?.category ||
          categories.find(
            (c) => c.id.toString() === formData.categoryID.toString(),
          )?.en_category ||
          formData.categoryID;
        onSaved({
          title: payload.title,
          mainText: payload.mainText,
          category,
          categoryID: payload.categoryID,
          coverImage: payload.coverImage,
          date: payload.date,
          edited: true,
        });
        onClose();
      } else {
        setMessage({
          type: "error",
          text: res.data || "Failed to edit article. Please try again.",
        });
      }
    } catch (err) {
      console.error("Error editing article:", err);
      setMessage({
        type: "error",
        text: err.response?.data || "Failed to edit article. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="ReportPopupWindow">
      <div className="backgroundBlock" onClick={onClose}></div>
      <div className="ReportForm edit-form-modal">
        <h1 className="TitleReport">Edit Article</h1>
        {loading ? (
          <p className="GrayText">Loading article data...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="edit-form-group">
              <label>Category *</label>
              <select
                name="categoryID"
                value={formData.categoryID}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.en_category ||
                      category.category ||
                      category.mal_category ||
                      "Unnamed Category"}
                  </option>
                ))}
              </select>
            </div>

            <div className="edit-form-group">
              <label>Article Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                maxLength={255}
                className="form-input"
                placeholder="Enter article title..."
              />
            </div>

            <div className="edit-form-group">
              <label>Cover Image</label>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
              >
                <input
                  id="editCoverImageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageChange}
                  disabled={uploadingCoverImage}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="editCoverImageInput"
                  className="add-image-btn"
                  style={{
                    cursor: uploadingCoverImage ? "not-allowed" : "pointer",
                    opacity: uploadingCoverImage ? 0.6 : 1,
                  }}
                >
                  <FontAwesomeIcon icon={faImage} style={{ marginRight: "0.5rem" }} />
                  {uploadingCoverImage
                    ? "Uploading..."
                    : formData.coverImage
                      ? "Change Cover Image"
                      : "Upload Cover Image"}
                </label>
                {formData.coverImage && (
                  <div className="image-preview-container">
                    <div className="image-preview-wrapper">
                      <img
                        src={formData.coverImage}
                        alt="Cover preview"
                        className="image-preview"
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, coverImage: "" }))
                        }
                        style={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="edit-form-group">
              <label>Article Content *</label>
              <MarkdownEditor
                value={formData.mainText}
                onChange={handleMarkdownChange}
                rows={12}
                ariaLabel="Article Content"
                placeholder="Write your article content here..."
              />
            </div>

            <div className="edit-form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="edit-form-group">
              <label>Attached Images</label>
              <div className="images-section">
                {formData.attachedImage.map((image, index) => (
                  <div
                    key={index}
                    className="image-preview-container attached-image-preview"
                  >
                    <div className="image-preview-wrapper">
                      <img
                        src={image.link}
                        alt={image.name}
                        className="image-preview attached-image"
                        onClick={() => setModalImage(image.link)}
                        style={{ cursor: "pointer" }}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageRemove(index);
                        }}
                        style={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                ))}
                <input
                  id="editAttachedImageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleAttachedImageAdd}
                  disabled={uploadingAttachedImage}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="editAttachedImageInput"
                  className="add-image-btn"
                  style={{
                    cursor: uploadingAttachedImage ? "not-allowed" : "pointer",
                    opacity: uploadingAttachedImage ? 0.6 : 1,
                  }}
                >
                  <FontAwesomeIcon icon={faImage} style={{ marginRight: "0.5rem" }} />
                  {uploadingAttachedImage ? "Uploading..." : "Add Image"}
                </label>
              </div>
            </div>

            {message.text && (
              <div className={`message ${message.type}`}>{message.text}</div>
            )}

            <div className="edit-form-actions">
              <button
                type="button"
                className="submutReportButton edit-cancel-btn"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submutReportButton"
                disabled={submitting}
                style={{ background: "var(--primary-500)" }}
              >
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>

      {modalImage && (
        <div className="image-modal-overlay" onClick={() => setModalImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="image-modal-close"
              onClick={() => setModalImage(null)}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <img
              src={modalImage}
              alt="Full size preview"
              className="image-modal-image"
            />
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

export default ArticleEditModal;